import { useState } from "react";
import { Upload } from "@formily/antd-v5";
import { useField } from "@formily/react";
import { Space, message } from "antd";
import { useTranslation } from "react-i18next";
import PhotoCapture, { cameraSupported } from "./PhotoCapture";
import { useAppSettings } from "../api/appSettings";
import DocumentPreview from "./DocumentPreview";
import { DOMAIN } from "../constants";

/**
 * The `Upload` every form field renders: antd's uploader, plus a "Take photo"
 * button when this deployment allows one.
 *
 * Registered under the name `Upload` in the schema-field component map, so no
 * stored form schema has to change — the 9 upload fields already in production
 * gain the camera without an administrator re-editing a single form.
 *
 * The camera button appears when all of these hold:
 *  - `photo_capture_enabled` is on in Django admin (the client's own switch);
 *  - the field is editable (a read-only permit is a record, not an input);
 *  - the device has a camera path at all.
 *
 * A field can opt out with `x-component-props: { camera: false }` — a
 * certificate-scan field where a phone snapshot is not acceptable evidence.
 */

type Props = {
  value?: any[];
  onChange?: (value: any[]) => void;
  /** Set by `prepareSchema`: the attachment endpoint and its auth header. */
  action?: string;
  headers?: Record<string, string>;
  /** False on a read-only render. */
  photoCapture?: boolean;
  /** Per-field override from the form designer. */
  camera?: boolean;
  multiple?: boolean;
  [key: string]: any;
};

const UploadWithCamera = (props: Props) => {
  const { t } = useTranslation();
  const settings = useAppSettings();
  // The live field value. `props.value` is captured when `upload` is called and
  // goes stale across the await, so a second photo started while the first is
  // still in flight would overwrite it.
  const field = useField<any>();
  const currentFiles = (): any[] =>
    Array.isArray(field?.value) ? field.value : [];
  const [uploading, setUploading] = useState(false);
  // The attachment currently open in the viewer.
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(
    null,
  );
  const { photoCapture, camera, ...uploadProps } = props;

  /**
   * Open an attachment instead of downloading it.
   *
   * antd's default for a text-list upload is a plain link, so a scanned
   * isolation certificate or a site drawing could only be inspected by saving
   * it and finding it again — the same round trip the generated permit had.
   * Supplying `onPreview` turns the filename into a viewer.
   *
   * A file still uploading has no URL yet, and one that failed has nothing to
   * show; both keep antd's own behaviour rather than opening an empty panel.
   */
  const previewFile = (file: any) => {
    const url: string | undefined = file?.url ?? file?.response?.file;
    if (!url) return;
    setPreview({
      // `attachment.file` is absolute where DRF had the request in context and
      // relative where it did not; resolving covers both.
      url: /^https?:\/\//i.test(url) ? url : `${DOMAIN}${url}`,
      name: file?.name ?? t("Attachment"),
    });
  };

  const show =
    settings.photo_capture_enabled &&
    photoCapture !== false &&
    camera !== false &&
    cameraSupported();

  /**
   * Upload one photo and add it to the field.
   *
   * Deliberately not routed through antd's own uploader: that would mean
   * reaching into rc-upload's instance to inject a file it was never given by
   * an input element. Posting it here is the same multipart request antd would
   * have made, and it leaves the progress and failure handling somewhere we can
   * read.
   */
  const upload = async (file: File) => {
    const action = props.action;
    if (!action) {
      message.error(t("Attachments are not configured for this form."));
      return;
    }
    const uid = `photo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const previewUrl = URL.createObjectURL(file);

    // Show it immediately, as uploading. A photo that vanishes for four seconds
    // on a slow site connection gets taken again, and again.
    props.onChange?.([
      ...currentFiles(),
      { uid, name: file.name, status: "uploading", percent: 0, thumbUrl: previewUrl },
    ]);
    setUploading(true);

    // Swap the placeholder for the real thing, leaving anything else that
    // arrived in the meantime where it is.
    const replace = (item: any) =>
      props.onChange?.(
        currentFiles().map((f: any) => (f?.uid === uid ? item : f))
      );

    try {
      const body = new FormData();
      body.append("file", file, file.name);
      const response = await fetch(action, {
        method: "POST",
        headers: props.headers ?? {},
        body,
      });
      if (!response.ok) throw new Error(String(response.status));
      const attachment = await response.json();
      replace({
        uid,
        name: file.name,
        status: "done",
        id: attachment.id,
        url: attachment.file,
        // Keep showing the local copy: it is already decoded, and the server
        // URL may be slow or (behind a proxy without X-Forwarded-Proto) mixed
        // content. Revoked on the next page load, which is soon enough.
        thumbUrl: previewUrl,
      });
    } catch {
      message.error(t("The photo could not be uploaded."));
      replace({
        uid,
        name: file.name,
        status: "error",
        thumbUrl: previewUrl,
      });
    } finally {
      setUploading(false);
    }
  };

  /* An attachment is served as a static file, so it is fetched without the JWT
     — see fetchFile. The viewer draws PDFs and images and offers a download for
     anything else. */
  const viewer = preview && (
    <DocumentPreview
      open
      onClose={() => setPreview(null)}
      title={preview.name}
      url={preview.url}
      authenticated={false}
      downloadName={preview.name}
    />
  );

  if (!show) {
    return (
      <>
        <Upload {...uploadProps} onPreview={previewFile} />
        {viewer}
      </>
    );
  }

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Upload {...uploadProps} onPreview={previewFile} />
      <PhotoCapture
        maxDimension={settings.photo_max_dimension}
        onCapture={upload}
        disabled={uploading || props.disabled}
        multiple={props.multiple}
      />
      {viewer}
    </Space>
  );
};

export default UploadWithCamera;
